import React, { useEffect, useState } from "react";
import { ShieldCheck, Sparkles } from "lucide-react";
import bingopayLogo from "../Assets/Images/logo_png_with_shreebalajee (1).png";

const FullScreenLoader = ({ 
  message = "Securing session & loading terminal...", 
  subMessage = "Please wait while we connect to banking servers" 
}) => {
  const [progress, setProgress] = useState(15);

  // Smooth simulated progress bar
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        const diff = Math.random() * 15;
        return Math.min(prev + diff, 90);
      });
    }, 300);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="fixed inset-0 z-[99999] flex flex-col items-center justify-between bg-gradient-to-b from-[#07192C] via-[#0A2540] to-[#051324] text-white p-6 select-none overflow-hidden font-sans">
      
      {/* Background Ambient Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-[500px] h-64 bg-blue-600/10 rounded-full blur-[140px] pointer-events-none" />

      {/* Top Status Pill */}
      <div className="pt-6 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md text-xs font-semibold text-slate-300 shadow-sm">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
          <span className="text-[11px] uppercase tracking-wider text-amber-200/90 font-bold">
            Live Terminal
          </span>
        </div>
      </div>

      {/* Center Logo Showcase with Light Sweep & Orbital Rings */}
      <div className="relative flex flex-col items-center justify-center -mt-8">
        
        {/* Outer Rotating Halo Ring */}
        <div className="absolute w-72 h-72 sm:w-80 sm:h-80 rounded-full border border-amber-400/20 border-t-amber-400/80 animate-spin [animation-duration:3s] pointer-events-none" />
        
        {/* Inner Counter-Rotating Ring */}
        <div className="absolute w-60 h-60 sm:w-68 sm:h-68 rounded-full border border-blue-400/10 border-b-blue-400/60 animate-spin [animation-duration:5s] [animation-direction:reverse] pointer-events-none" />

        {/* Logo Card Container */}
        <div className="relative z-10 px-8 py-6 rounded-3xl bg-white/95 backdrop-blur-xl shadow-2xl shadow-black/40 border border-white/40 flex flex-col items-center max-w-sm sm:max-w-md mx-4 transition-transform animate-pulse [animation-duration:2.5s]">
          
          {/* Shimmer Light Reflection Overlay */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
            <div className="w-full h-full bg-gradient-to-r from-transparent via-amber-200/20 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
          </div>

          {/* Logo Image */}
          <img
            src={bingopayLogo}
            alt="BingoPay"
            className="w-56 sm:w-72 object-contain drop-shadow-md"
          />
        </div>

        {/* Dynamic Loading Text & Progress */}
        <div className="mt-10 text-center space-y-3 z-10 max-w-xs sm:max-w-sm">
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-400 animate-bounce" />
            <p className="text-sm font-bold text-slate-100 tracking-wide">
              {message}
            </p>
          </div>
          
          <p className="text-xs text-slate-400 font-medium leading-relaxed">
            {subMessage}
          </p>

          {/* Precision Fintech Progress Bar */}
          <div className="w-full bg-slate-800/80 rounded-full h-1.5 p-0.5 overflow-hidden border border-white/10 mt-3 shadow-inner">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-amber-400 to-yellow-500 transition-all duration-300 ease-out shadow-xs shadow-amber-400/50"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Footer Security Seal */}
      <div className="pb-4 text-center z-10 flex items-center justify-center gap-2 text-slate-400 text-xs font-semibold">
        <ShieldCheck className="w-4 h-4 text-emerald-400" />
        <span>256-Bit SSL Encrypted Banking Connection</span>
      </div>

      {/* Custom Keyframe for shimmer */}
      <style>{`
        @keyframes shimmer {
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </div>
  );
};

export default FullScreenLoader;